/**
 * Map readiness & layer retry utilities
 * ------------------------------------
 * Centralized helpers to:
 * - Wait for Mapbox style readiness in an event-driven way.
 * - Wrap layer operations with limited automatic retries and DRY error handling.
 * - Detect style reloads and mark layers as stale for re-initialization.
 *
 * These helpers are intentionally framework-agnostic and operate on the
 * singleton map instance plus the zustand map store for status tracking.
 */
import type { Map as MapboxMap } from 'mapbox-gl';
import { useMapStore } from '@/stores/slices/useMapStore';

export type LayerStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface LayerOperationResult {
  success: boolean;
  attempts: number;
  error?: Error;
}

// ─────────────────────────────────────────────────────────────────────────────
// Style Reload Detection
// ─────────────────────────────────────────────────────────────────────────────

/** Track if we've already attached a style.load listener */
let styleReloadListenerAttached = false;

/**
 * Attach a one-time style.load listener that resets layer statuses when the
 * Mapbox style reloads (e.g. HMR, user-triggered style change). This allows
 * hooks to detect stale layers and re-initialize them.
 *
 * Safe to call multiple times; only attaches once per map instance.
 */
export function attachStyleReloadListener(mapInstance: MapboxMap): void {
  if (styleReloadListenerAttached) return;
  styleReloadListenerAttached = true;

  mapInstance.on('style.load', () => {
    const store = useMapStore.getState();
    store.resetLayerStatus();

    // Also reset layer registry so layers can be re-initialized
    // Import dynamically to avoid circular dependency
    import('@/services/map/layerRegistry').then(({ layerRegistry }) => {
      layerRegistry.reset();
    });
  });
}

/**
 * Wait until the Mapbox style is fully loaded using events,
 * with a safety timeout. This avoids ad‑hoc polling and works well
 * even if the user interacts quickly.
 */
export async function waitForMapReady(mapInstance: MapboxMap | null, timeoutMs: number = 5000): Promise<boolean> {
  if (!mapInstance) return false;

  // Fast-path: style already loaded
  if (mapInstance.isStyleLoaded()) {
    return true;
  }

  return new Promise((resolve) => {
    let resolved = false;

    const cleanup = () => {
      if (resolved) return;
      resolved = true;
      mapInstance.off('idle', onIdle);
      window.clearTimeout(timeoutId);
    };

    const onIdle = () => {
      if (mapInstance.isStyleLoaded()) {
        cleanup();
        resolve(true);
      }
    };

    const timeoutId = window.setTimeout(() => {
      // As a final check, if the style is actually loaded, treat as ready.
      if (mapInstance.isStyleLoaded()) {
        cleanup();
        resolve(true);
      } else {
        cleanup();
        resolve(false);
      }
    }, timeoutMs);

    mapInstance.on('idle', onIdle);
  });
}

interface WithMapLayerRetryOptions<T> {
  layerKey: string;
  map: MapboxMap | null;
  /**
   * Actual operation that adds/updates layers or sources.
   * It will only be called after the style is confirmed ready.
   */
  attempt: (map: MapboxMap) => Promise<T> | T;
  /**
   * Maximum number of retries after the first attempt.
   * Total attempts = maxRetries + 1.
   */
  maxRetries?: number;
  /**
   * Initial backoff delay in milliseconds.
   * Each subsequent retry uses delay * 2.
   */
  initialDelayMs?: number;
}

/**
 * Wrap a layer operation with:
 * - Style readiness checks.
 * - Limited automatic retries with simple exponential backoff.
 * - Centralized status updates in the map store.
 */
export async function withMapLayerRetry<T>(options: WithMapLayerRetryOptions<T>): Promise<LayerOperationResult> {
  const { layerKey, map, attempt, maxRetries = 2, initialDelayMs = 500 } = options;

  const store = useMapStore.getState();
  store.setLayerStatus(layerKey, 'loading');

  if (!map) {
    const error = new Error('Map instance is not available');
    store.setLayerStatus(layerKey, 'error', error.message);
    return { success: false, attempts: 0, error };
  }

  let attemptIndex = 0;
  let lastError: Error | undefined;

  const totalAttempts = Math.max(1, maxRetries + 1);

  while (attemptIndex < totalAttempts) {
    attemptIndex += 1;

    const ready = await waitForMapReady(map, 5000);
    if (!ready) {
      lastError = new Error('Map style not ready');
    } else {
      try {
        await attempt(map);
        store.setLayerStatus(layerKey, 'ready');
        return { success: true, attempts: attemptIndex };
      } catch (err) {
        lastError = err as Error;
      }
    }

    // If we still have retries left, back off before next attempt
    if (attemptIndex < totalAttempts) {
      const delay = initialDelayMs * 2 ** (attemptIndex - 1);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  // All attempts failed
  const message = lastError?.message || 'Unknown layer operation error';
  store.setLayerStatus(layerKey, 'error', message);

  return {
    success: false,
    attempts: attemptIndex,
    error: lastError,
  };
}

/**
 * Convenience helper for common "add or update" layer flows.
 * Keeps call sites very small and consistent.
 */
export async function addLayerWithReadiness<T>(
  layerKey: string,
  mapInstance: MapboxMap | null,
  addFn: (map: MapboxMap) => Promise<T> | T,
  options?: { maxRetries?: number; initialDelayMs?: number },
): Promise<LayerOperationResult> {
  return withMapLayerRetry<T>({
    layerKey,
    map: mapInstance,
    attempt: addFn,
    maxRetries: options?.maxRetries,
    initialDelayMs: options?.initialDelayMs,
  });
}
